import { SqliteExecutor } from './SqliteExecutor';
import * as path from 'path';
import fs from 'fs';

const rootPath = path.resolve(process.cwd(), '.dbcube');

interface DatabaseConfig {
    name?: string;
    HOST?: string;
    USER?: string;
    PASSWORD?: string;
    DATABASE?: string;
    PORT?: number;
}

interface QueryResult {
    status: 'success' | 'error';
    message: string;
    data: any | null;
}

interface RunResult {
    changes: number;
    lastID: number;
}

interface ParametrizedQueryResult {
    query: string;
    parameters: any[];
}

class SQLite {
    private executor: SqliteExecutor | null = null;
    private database?: string;
    
    constructor(config: DatabaseConfig) {
        this.database = config.DATABASE;
    }

    async ifExist(): Promise<Boolean> {
        if (this.database) {
            const dbPath = this.database || ':memory:';
            const configPath = path.join(rootPath, dbPath + ".db");
            
            // Create .dbcube directory if it doesn't exist
            if (!fs.existsSync(rootPath)) {
                fs.mkdirSync(rootPath, { recursive: true });
            }
            
            if (fs.existsSync(configPath)) {
                return true;
            }
            
            // También verificar usando el executor
            if (!this.executor) {
                this.executor = new SqliteExecutor(configPath);
            }
            return await this.executor.exists();
        }
        return false;
    }

    async connect(): Promise<SqliteExecutor> {
        return new Promise(async (resolve, reject) => {
            try {
                if (!this.executor) {
                    const dbPath = this.database || ':memory:';
                    const configPath = path.join(rootPath, dbPath + ".db");
                    
                    // Create .dbcube directory if it doesn't exist
                    if (!fs.existsSync(rootPath)) {
                        fs.mkdirSync(rootPath, { recursive: true });
                    }
                    
                    this.executor = new SqliteExecutor(configPath);
                    const connected = await this.executor.connect();
                    
                    if (!connected) {
                        throw new Error('Failed to connect to SQLite database');
                    }
                }
                resolve(this.executor);
            } catch (error) {
                reject(error);
            }
        });
    }

    async disconnect(): Promise<void> {
        return new Promise((resolve) => {
            if (this.executor) {
                // SQLiteExecutor no necesita cerrar conexión persistente
                this.executor = null;
            }
            resolve();
        });
    }

    async query(sqlQuery: string): Promise<QueryResult> {
        return new Promise(async (resolve) => {
            try {
                if (typeof sqlQuery !== 'string') {
                    throw new Error('The SQL query must be a string.');
                }
                
                if (!this.executor) {
                    await this.connect();
                }
                
                if (!this.executor) {
                    throw new Error('Database connection is not available.');
                }
                
                // Usar queryMultiple que maneja múltiples comandos SQL
                const result = await this.executor.queryMultiple(sqlQuery);
                
                if (result.status === 'error') {
                    resolve({
                        status: 'error',
                        message: result.message,
                        data: null,
                    });
                } else {
                    resolve({
                        status: 'success',
                        message: 'Query executed successfully',
                        data: result.data,
                    });
                }
            } catch (error) {
                resolve({
                    status: 'error',
                    message: (error as Error).message || 'An error occurred while executing the query.',
                    data: null,
                });
            }
        });
    }

    async queryWithParameters(sqlQuery: string, params: any[] = []): Promise<QueryResult> {
        return new Promise(async (resolve) => {
            try {
                if (typeof sqlQuery !== 'string') {
                    throw new Error('The SQL query must be a string.');
                }
                
                if (!Array.isArray(params)) {
                    throw new Error('Parameters must be an array.');
                }
                
                if (!this.executor) {
                    await this.connect();
                }
                
                if (!this.executor) {
                    throw new Error('Database connection is not available.');
                }
                
                const result = await this.executor.query(sqlQuery, params);
                
                if (result.status === 'error') {
                    resolve({
                        status: 'error',
                        message: result.message,
                        data: null,
                    });
                } else {
                    resolve({
                        status: 'success',
                        message: 'Query executed successfully',
                        data: result.data,
                    });
                }
            } catch (error) {
                console.log(error);
                resolve({
                    status: 'error',
                    message: (error as Error).message || 'An error occurred while executing the query.',
                    data: null,
                });
            }
        });
    }

    convertToParameterizedQuery(sql: string): ParametrizedQueryResult {
        const normalizedSql = sql.replace(/\s+/g, ' ').trim();
        
        const baseQueryMatch = normalizedSql.match(/^(.+?)\s+VALUES\s*\(/i);
        if (!baseQueryMatch) {
            throw new Error('No se pudo encontrar la estructura VALUES en la consulta');
        }
        
        const baseQuery = baseQueryMatch[1];
        
        const valuesStartIndex = normalizedSql.toUpperCase().indexOf('VALUES');
        const valuesSection = normalizedSql.substring(valuesStartIndex);
        const valuesMatch = valuesSection.match(/VALUES\s*\((.+)\)\s*;?\s*$/i);
        if (!valuesMatch) {
            throw new Error('No se pudieron extraer los valores de la consulta');
        }
        
        const valuesString = valuesMatch[1];
        
        function parseValues(str: string): any[] {
            const values: any[] = [];
            let currentValue = '';
            let inQuotes = false;
            let quoteChar = '';
            let inCompute = false;
            let computeDepth = 0;
            
            for (let i = 0; i < str.length; i++) {
                const char = str[i];
                const prevChar = str[i - 1];
                
                if (!inQuotes && str.substring(i, i + 8) === '@compute') {
                    inCompute = true;
                }
                
                if (inCompute && char === '(') {
                    computeDepth++;
                } else if (inCompute && char === ')') {
                    computeDepth--;
                    if (computeDepth === 0) {
                        inCompute = false;
                    }
                }
                
                if (!inCompute && (char === '"' || char === "'") && prevChar !== '\\') {
                    if (!inQuotes) {
                        inQuotes = true;
                        quoteChar = char;
                    } else if (char === quoteChar) {
                        if (str[i + 1] === quoteChar) {
                            currentValue += char + char;
                            i++;
                            continue;
                        } else {
                            inQuotes = false;
                            quoteChar = '';
                        }
                    }
                }
                
                if (!inQuotes && !inCompute && char === ',') {
                    values.push(cleanValue(currentValue.trim()));
                    currentValue = '';
                    continue;
                }
                
                currentValue += char;
            }
            
            if (currentValue.trim()) {
                values.push(cleanValue(currentValue.trim()));
            }
            
            return values;
        }
        
        function cleanValue(value: string): any {
            value = value.trim();
            
            if (value.startsWith('@compute')) {
                return value;
            }
            
            if ((value.startsWith('"') && value.endsWith('"')) || 
                (value.startsWith("'") && value.endsWith("'"))) {
                value = value.slice(1, -1);
                value = value.replace(/''/g, "'").replace(/""/g, '"');
            }
            
            return value;
        }
        
        const parameters = parseValues(valuesString);
        const placeholders = parameters.map(() => '?').join(', ');
        const parametrizedQuery = `${baseQuery} VALUES (${placeholders});`;
        
        return {
            query: parametrizedQuery,
            parameters: parameters
        };
    }
}

export const DbConfig = new SQLite({DATABASE: "config"});
export default DbConfig;